import {
  MEMBER_ROLE_LABELS,
  MEMBER_ROLE_OPTIONS,
  formatAmount,
  formatPhoneEmail,
} from '../futures/futuresOrderConstants';
import {
  inputDateTimeToQueryApi,
  toInputDateTime,
} from '../futures/futuresRoundConstants';

/** 交易類型（對齊 TransactionType） */
export const TRANSACTION_TYPE_OPTIONS = [
  { value: 'RECHARGE_SYSTEM', label: '系統上分' },
  { value: 'RECHARGE_ONLINE', label: '線上充值' },
  { value: 'RECHARGE_OFFLINE', label: '離線充值' },
  { value: 'WITHDRAWAL_SYSTEM', label: '系統下分' },
  { value: 'WITHDRAWAL_ONLINE', label: '線上提款' },
  { value: 'WITHDRAWAL_OFFLINE', label: '線下提現' },
  { value: 'WITHDRAWAL_MEMBER', label: '用戶提現' },
  { value: 'WITHDRAW_RETURN', label: '取消用戶提現退款' },
  { value: 'ORDER_BET', label: '下單扣款' },
  { value: 'ORDER_PAYOUT', label: '派彩入帳' },
  { value: 'ORDER_REFUND', label: '撤單退款' },
  { value: 'ORDER_FEE', label: '下單手續費' },
  { value: 'FUTURE_ORDER', label: '交割合約委託' },
  { value: 'FUTURE_FOLLOW', label: '交割跟單委託' },
  { value: 'FUTURE_ORDER_CANCEL', label: '交割合約撤單' },
  { value: 'FUTURE_EXECUTION', label: '交割合約結算' },
  { value: 'TIME_LIMITED_ORDER', label: '限時委託' },
  { value: 'TIME_LIMITED_EXECUTION', label: '限時結算' },
  { value: 'TIME_LIMITED_REFUND', label: '限時失敗退款' },
  { value: 'PERPETUAL_ORDER', label: '永續合約委託' },
  { value: 'PERPETUAL_ORDER_CANCEL', label: '永續合約撤單' },
  { value: 'PERPETUAL_EXECUTION', label: '永續合約平倉結算' },
  { value: 'PERPETUAL_FEE', label: '永續合約手續費' },
  { value: 'PERPETUAL_PROFIT', label: '永續合約盈利' },
  { value: 'PERPETUAL_LOSS', label: '永續合約虧損' },
  { value: 'PERPETUAL_LIQUIDATION', label: '永續合約強平' },
];

export const TRANSACTION_TYPE_LABELS = Object.fromEntries(
  TRANSACTION_TYPE_OPTIONS.map((item) => [item.value, item.label])
);

export { MEMBER_ROLE_LABELS, MEMBER_ROLE_OPTIONS, formatAmount, formatPhoneEmail };

const MAX_TRANSACTION_QUERY_DAYS = 31;

export function defaultTransactionTimeRange() {
  const now = new Date();
  const to = new Date(now);
  to.setHours(23, 59, 0, 0);

  const from = new Date(now);
  from.setDate(from.getDate() - 7);
  from.setHours(0, 0, 0, 0);

  return {
    createTimeFromInput: toInputDateTime(from),
    createTimeToInput: toInputDateTime(to),
  };
}

function calendarDaysBetween(fromInput, toInput) {
  const fromDate = fromInput.slice(0, 10);
  const toDate = toInput.slice(0, 10);
  const from = new Date(`${fromDate}T00:00:00`);
  const to = new Date(`${toDate}T00:00:00`);
  return Math.round((to - from) / (24 * 60 * 60 * 1000));
}

export function validateTransactionTimeRange(fromInput, toInput) {
  if (!fromInput || !toInput) {
    return '';
  }
  const fromApi = inputDateTimeToQueryApi(fromInput);
  const toApi = inputDateTimeToQueryApi(toInput);
  if (fromApi > toApi) {
    return '交易時間起不可晚於迄';
  }
  const days = calendarDaysBetween(fromInput, toInput);
  if (days > MAX_TRANSACTION_QUERY_DAYS) {
    return `交易時間跨度不可超過 ${MAX_TRANSACTION_QUERY_DAYS} 天`;
  }
  return '';
}

export function buildTransactionPageParams(pageNo, filters) {
  const params = {
    pageNo,
    pageSize: 15,
  };

  if (filters.inviterId?.trim()) {
    params.inviterId = Number(filters.inviterId);
  }
  if (filters.memberId?.trim()) {
    params.memberId = Number(filters.memberId);
  }
  if (filters.depositOrderId?.trim()) {
    params.depositOrderId = Number(filters.depositOrderId);
  }
  if (filters.withdrawOrderId?.trim()) {
    params.withdrawOrderId = Number(filters.withdrawOrderId);
  }
  if (filters.orderSerialId?.trim()) {
    params.orderSerialId = filters.orderSerialId.trim();
  }
  if (filters.roleId) {
    params.roleId = Number(filters.roleId);
  }
  if (filters.phoneEmail?.trim()) {
    params.phoneEmail = filters.phoneEmail.trim();
  }
  if (filters.types?.length) {
    params.types = filters.types.join(',');
  }
  if (filters.createTimeFromInput) {
    params.createTimeFrom = inputDateTimeToQueryApi(filters.createTimeFromInput);
  }
  if (filters.createTimeToInput) {
    params.createTimeTo = inputDateTimeToQueryApi(filters.createTimeToInput);
  }

  return params;
}

export function formatTransactionType(type) {
  if (!type) {
    return '—';
  }
  return TRANSACTION_TYPE_LABELS[type] || type;
}

export function formatOptionalId(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

export function splitRemarkHistory(remark) {
  if (!remark) {
    return [];
  }
  return String(remark)
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);
}

const REMARK_ENTRY_PATTERN = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) - (.*)$/;

/** 解析備註歷程（日期 + 內容） */
export function parseRemarkHistoryEntries(remark) {
  return splitRemarkHistory(remark).map((entry) => {
    const matched = entry.match(REMARK_ENTRY_PATTERN);
    if (matched) {
      return {
        createTime: matched[1],
        content: matched[2],
      };
    }
    return {
      createTime: '',
      content: entry,
    };
  });
}

/** 列表顯示：最新一筆備註內容 */
export function parseCurrentRemark(remark) {
  const entries = parseRemarkHistoryEntries(remark);
  if (entries.length === 0) {
    return '';
  }
  return entries[0].content || '';
}

export function hasRemarkHistory(remark) {
  return parseRemarkHistoryEntries(remark).length > 0;
}
